import { useState } from "react";
import { BarChart3, ChevronDown, ChevronRight } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Cell, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine } from "recharts";
import { fmtTL, fmtNum } from "../../lib/format";
import { METRICS } from "./YearlyTable";

const MON = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const PALETTE = ["#b45309", "#0369a1", "#047857", "#7c3aed", "#be123c", "#0f172a", "#ca8a04", "#0e7490"];
const MAX_LINES = 8;
const short = (n) => (Math.abs(n) >= 1e6 ? `${fmtNum(n / 1e6, 1)} M` : Math.abs(n) >= 1e3 ? `${fmtNum(n / 1e3, 0)} B` : fmtNum(n, 0));

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-md px-3 py-2 text-xs max-w-xs" data-testid="pcsum-year-chart-tooltip">
      <div className="font-bold text-slate-900 mb-1">{label}</div>
      {payload.map((p) => <div key={p.dataKey} className="flex justify-between gap-4"><span style={{ color: p.color }} className="truncate">{p.name}</span><b className={`font-mono ${p.value < 0 ? "text-red-700" : ""}`}>{fmtTL(p.value)}</b></div>)}
    </div>
  );
};

// Yıllık grafik: çubuk = tüm projelerin aylık kâr/zarar (yeşil / kırmızı); çizgiler = proje bazlı aylık trend (seçili ölçüt, en büyük 8 proje; lejanttan gizle/göster)
export default function YearlyChart({ data, metric }) {
  const [open, setOpen] = useState(true);
  const [hidden, setHidden] = useState(() => new Set());
  if (!data?.projects?.length) return null;
  const top = [...data.projects].sort((a, b) => Math.abs(b.total[metric]) - Math.abs(a.total[metric])).slice(0, MAX_LINES);
  const rows = data.months.map((mo, i) => ({
    name: MON[i], fark: data.grand.months[mo].fark,
    ...Object.fromEntries(top.map((p) => [p.id, p.months[mo][metric]])),
  }));
  const toggle = (e) => { const k = e.dataKey; setHidden((h) => { const n = new Set(h); n.has(k) ? n.delete(k) : n.add(k); return n; }); };
  const total = data.grand.total.fark;
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="pcsum-year-chart">
      <button type="button" onClick={() => setOpen(!open)} data-testid="pcsum-year-chart-toggle" className="w-full flex items-center gap-2 px-4 py-2.5 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <BarChart3 className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-bold text-slate-900">{data.year} Aylık Kâr/Zarar ve Proje Trendi</span>
        <span className="text-[11px] text-slate-500 hidden md:inline">· çubuk = tüm projeler fark · çizgi = proje bazlı {METRICS[metric].toLowerCase()}{data.projects.length > MAX_LINES ? ` (en büyük ${MAX_LINES} proje)` : ""} · lejanta tıklayınca gizlenir</span>
        <span className={`ml-auto text-xs font-mono font-bold ${total < 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="pcsum-year-chart-total">Yıl: {fmtTL(total)}</span>
      </button>
      {open && (
        <div className="px-2 pb-3" style={{ height: 340 }} data-testid="pcsum-year-chart-body">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 16, left: 8, bottom: 8 }} barCategoryGap="28%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#334155" }} interval={0} />
              <YAxis tickFormatter={short} tick={{ fontSize: 10, fill: "#64748b" }} width={68} />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Tooltip content={<Tip />} cursor={{ fill: "rgba(245,158,11,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 11, cursor: "pointer" }} onClick={toggle} formatter={(v, e) => <span style={{ textDecoration: hidden.has(e.dataKey) ? "line-through" : "none", opacity: hidden.has(e.dataKey) ? 0.5 : 1 }}>{v}</span>} />
              <Bar dataKey="fark" name="Aylık Kâr/Zarar (tüm projeler)" fill="#047857" radius={[3, 3, 0, 0]} maxBarSize={44} hide={hidden.has("fark")}>
                {rows.map((r) => <Cell key={r.name} fill={r.fark < 0 ? "#dc2626" : "#047857"} />)}
              </Bar>
              {top.map((p, i) => <Line key={p.id} type="monotone" dataKey={p.id} name={p.name} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} hide={hidden.has(p.id)} connectNulls />)}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
